import { useEffect, useMemo } from "react";

import type { ProjectNode } from "../model/types/ProjectTree";
import type {
  ProjectSelectionController,
  ProjectSelectionState,
} from "../model/types/ProjectSelection";

type Params = {
  projects: ProjectNode[];
  selection: ProjectSelectionController;
  onSelectionChange?: (selection: ProjectSelectionState) => void;
  onValidityChange?: (isValid: boolean) => void;
};

export function useProjectFormController({
  projects,
  selection,
  onSelectionChange,
  onValidityChange,
}: Params) {
  const cascaded = useMemo(() => {
    const projectId = selection.state.project?.id;

    const project = projectId
      ? projects.find((p) => p.id === projectId)
      : undefined;

    const phaseId = selection.state.phase.at(-1)?.id;
    const phase = project?.phases.find((p) => p.id === phaseId);

    const blockId = selection.state.block.at(-1)?.id;
    const block = phase?.blocks.find((b) => b.id === blockId);

    return {
      phases: project?.phases ?? [],
      blocks: phase?.blocks ?? [],
      stages: block?.stages ?? [],
    };
  }, [
    projects,
    selection.state.project?.id,
    selection.state.phase,
    selection.state.block,
  ]);

  const isValid =
    selection.hasProject &&
    selection.hasPhase &&
    selection.hasBlock &&
    selection.hasStage;

  useEffect(() => {
    const projectId = selection.state.project?.id;
    if (!projectId) return;

    const project = projects.find((p) => p.id === projectId);
    if (!project) return;

    // validar phase
    const phaseId = selection.state.phase.at(-1)?.id;
    const phaseExists = project.phases.some((p) => p.id === phaseId);

    if (phaseId && !phaseExists) {
      selection.setPhase([]);
      selection.setBlock([]);
      selection.setStage([]);
      return;
    }

    // validar block
    const phase = project.phases.find((p) => p.id === phaseId);
    const blockId = selection.state.block.at(-1)?.id;
    const blockExists = phase?.blocks.some((b) => b.id === blockId);

    if (blockId && !blockExists) {
      selection.setBlock([]);
      selection.setStage([]);
      return;
    }

    // validar stage
    const block = phase?.blocks.find((b) => b.id === blockId);
    const stageId = selection.state.stage.at(-1)?.id;
    const stageExists = block?.stages.some((s) => s.id === stageId);

    if (stageId && !stageExists) {
      selection.setStage([]);
    }
  }, [
    projects,
    selection,
    selection.state.project?.id,
    selection.state.phase,
    selection.state.block,
    selection.state.stage,
  ]);

  useEffect(() => {
    onSelectionChange?.(selection.state);
  }, [selection.state, onSelectionChange]);

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  return {
    cascaded,
    isValid,
  };
}
